import type {
  IntegrationChannelCrmSettings,
  IntegrationChannelItem,
  IntegrationChannelSettings,
  IntegrationCrmSettings,
  IntegrationItem,
  IntegrationStatus,
  WazzupSettings,
} from '@/api/integrations/integrations.types'
import {
  canConnectVkCrmIntegration,
  createDefaultVkCrmSettings,
  getIntegrationsListErrorText,
  isMaskedSecret,
  MASKED_SECRET_PLACEHOLDER,
  getCredentialInputValue,
  mergeVkCrmSettingsForSave,
  normalizeIntegrationFunnelId,
  normalizeIntegrationTrafficChannelId,
  VK_CRM_STATUS_LABELS,
  VK_CRM_USAGE,
} from '../../VkCrmCommunitiesSettings/lib/vkCrmIntegration.helpers'

export const WAZZUP_CRM_USAGE = VK_CRM_USAGE

/** Wazzup из CRM всегда держит оба usage — чаты + линии для WA-уведомлений. */
export const WAZZUP_NOTIFICATION_USAGE = 'notifications' as const

export const WAZZUP_CRM_AND_NOTIFICATIONS_USAGES = [
  WAZZUP_CRM_USAGE,
  WAZZUP_NOTIFICATION_USAGE,
] as const

export const WAZZUP_CRM_STATUS_LABELS = VK_CRM_STATUS_LABELS

export const canConnectWazzupCrmIntegration = canConnectVkCrmIntegration
export const createDefaultWazzupCrmSettings = createDefaultVkCrmSettings
export const mergeWazzupCrmSettingsForSave = mergeVkCrmSettingsForSave
export {
  getIntegrationsListErrorText,
  isMaskedSecret,
  MASKED_SECRET_PLACEHOLDER,
  getCredentialInputValue,
}

export const isWazzupCrmIntegration = (item: IntegrationItem): boolean => {
  if (item.provider !== 'wazzup') return false
  const usages = item.usages
  if (!Array.isArray(usages) || usages.length === 0) return true
  return usages.includes(WAZZUP_CRM_USAGE)
}

export const filterWazzupCrmIntegrations = (
  items: IntegrationItem[] | null | undefined,
): IntegrationItem[] => {
  if (!Array.isArray(items)) return []
  return items.filter(isWazzupCrmIntegration)
}

export const readWazzupCrmSettings = (
  item: IntegrationItem | null | undefined,
): IntegrationCrmSettings => {
  const crm = (item?.settings as WazzupSettings | undefined)?.crm
  return createDefaultVkCrmSettings({
    funnel_id: normalizeIntegrationFunnelId(crm?.funnel_id),
    traffic_channel_id: normalizeIntegrationTrafficChannelId(
      crm?.traffic_channel_id,
    ),
    create_lead: crm?.create_lead ?? true,
    create_chat: crm?.create_chat ?? true,
    merge_lead_by_phone: Boolean(crm?.merge_lead_by_phone),
    lead_fields:
      crm?.lead_fields && Array.isArray(crm.lead_fields) ? crm.lead_fields : [],
    access: {
      roles: crm?.access?.roles ?? [],
      departments: crm?.access?.departments ?? [],
    },
  })
}

const tryParseJson = <T>(value: unknown): T | null => {
  if (value == null || value === '') return null
  if (typeof value === 'object') return value as T
  if (typeof value !== 'string') return null
  try {
    return JSON.parse(value) as T
  } catch {
    return null
  }
}

/** Бэк отдаёт settings как JSON-строку или null (= наследует интеграцию). */
export const parseChannelSettings = (
  channel: IntegrationChannelItem | null | undefined,
): IntegrationChannelSettings | null => {
  if (!channel || channel.settings == null || channel.settings === '') {
    return null
  }
  const parsed = tryParseJson<
    IntegrationChannelSettings | IntegrationChannelCrmSettings
  >(channel.settings)
  if (!parsed || typeof parsed !== 'object') return null
  if ('crm' in parsed) {
    return parsed as IntegrationChannelSettings
  }
  // на случай если пришла сразу crm-обвязка без обёртки
  return { crm: parsed as IntegrationChannelCrmSettings }
}

export const parseChannelMetadata = (
  channel: IntegrationChannelItem | null | undefined,
): Record<string, unknown> | null => {
  if (!channel) return null
  return tryParseJson<Record<string, unknown>>(channel.metadata)
}

export const getChannelLocalId = (
  channel: IntegrationChannelItem | null | undefined,
): number | null => {
  if (!channel) return null
  const num = Number(channel.id)
  return Number.isFinite(num) && num > 0 ? num : null
}

/** `settings: null` / пусто → канал наследует CRM интеграции. */
export const channelInheritsIntegrationSettings = (
  channel: IntegrationChannelItem | null | undefined,
): boolean => {
  if (!channel || channel.settings == null || channel.settings === '') {
    return true
  }
  if (typeof channel.settings === 'string' && channel.settings.trim() === '{}') {
    return true
  }
  const parsed = parseChannelSettings(channel)
  if (!parsed) return true
  const crm = parsed.crm
  if (crm == null) return true
  return (
    crm.create_lead === undefined &&
    crm.create_chat === undefined &&
    crm.merge_lead_by_phone === undefined &&
    crm.funnel_id === undefined &&
    crm.traffic_channel_id === undefined
  )
}
export const channelHasOwnCrmSettings = (
  channel: IntegrationChannelItem | null | undefined,
): boolean => !channelInheritsIntegrationSettings(channel)

export type ResolvedChannelCrmSettings = {
  source: 'integration' | 'channel'
  crm: Required<
    Pick<
      IntegrationChannelCrmSettings,
      | 'create_lead'
      | 'create_chat'
      | 'merge_lead_by_phone'
      | 'funnel_id'
      | 'traffic_channel_id'
    >
  >
}

export const resolveChannelCrmSettings = (
  channel: IntegrationChannelItem,
  integrationDefaults: IntegrationCrmSettings,
): ResolvedChannelCrmSettings => {
  if (channelInheritsIntegrationSettings(channel)) {
    return {
      source: 'integration',
      crm: {
        create_lead: integrationDefaults.create_lead ?? true,
        create_chat: integrationDefaults.create_chat ?? true,
        merge_lead_by_phone: Boolean(
          integrationDefaults.merge_lead_by_phone,
        ),
        funnel_id: normalizeIntegrationFunnelId(integrationDefaults.funnel_id),
        traffic_channel_id: normalizeIntegrationTrafficChannelId(
          integrationDefaults.traffic_channel_id,
        ),
      },
    }
  }

  const parsed = parseChannelSettings(channel)
  const crm = parsed?.crm ?? null
  return {
    source: 'channel',
    crm: {
      create_lead: crm?.create_lead ?? integrationDefaults.create_lead ?? true,
      create_chat: crm?.create_chat ?? integrationDefaults.create_chat ?? true,
      merge_lead_by_phone:
        crm?.merge_lead_by_phone !== undefined
          ? Boolean(crm.merge_lead_by_phone)
          : Boolean(integrationDefaults.merge_lead_by_phone),
      funnel_id:
        normalizeIntegrationFunnelId(crm?.funnel_id) ??
        normalizeIntegrationFunnelId(integrationDefaults.funnel_id),
      traffic_channel_id:
        normalizeIntegrationTrafficChannelId(crm?.traffic_channel_id) ??
        normalizeIntegrationTrafficChannelId(
          integrationDefaults.traffic_channel_id,
        ),
    },
  }
}

/** @deprecated use resolveChannelCrmSettings */
export const resolveChannelCrmFormValues = (
  channel: IntegrationChannelItem,
  integrationDefaults: IntegrationCrmSettings,
) => resolveChannelCrmSettings(channel, integrationDefaults).crm


export const getWazzupChannelTypeLabel = (type: string): string => {
  const normalized = String(type ?? '')
    .trim()
    .toLowerCase()
  const map: Record<string, string> = {
    whatsapp: 'WhatsApp',
    wapi: 'WhatsApp API',
    waba: 'WABA',
    telegram: 'Telegram',
    tgapi: 'Telegram API',
    instagram: 'Instagram',
    max: 'MAX',
    maxbot: 'MAX',
    vk: 'ВКонтакте',
    vkontakte: 'ВКонтакте',
    viber: 'Viber',
    avito: 'Авито',
  }
  if (map[normalized]) {
    return map[normalized]
  }
  const raw = String(type ?? '').trim()
  return raw || 'неизвестный'
}

/** Wazzup passthrough status → единый ключ (case/snake/camel). */
export const normalizeWazzupChannelStatus = (status: unknown): string =>
  String(status ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, '')

/**
 * Подписи status из Wazzup (state/status passthrough).
 * Ключи — после normalizeWazzupChannelStatus.
 */
const WAZZUP_CHANNEL_STATUS_LABELS: Record<string, string> = {
  active: 'активен',
  connected: 'активен',
  init: 'запускается',
  disabled: 'отключён',
  inactive: 'неактивен',
  qr: 'нужен QR',
  qridle: 'QR протух',
  phoneunavailable: 'нет связи с телефоном',
  openelsewhere: 'авторизован в другом месте',
  notenoughmoney: 'не оплачен',
  foreignphone: 'QR с другого номера',
  unauthorized: 'нет авторизации',
  waitforpassword: 'нужен пароль 2FA',
  blocked: 'заблокирован',
  onmoderation: 'на модерации',
  rejected: 'отклонён',
  unknown: 'неизвестный статус',
  pending: 'ожидает',
}

export const getWazzupChannelStatusLabel = (status: string): string => {
  const normalized = normalizeWazzupChannelStatus(status)
  if (!normalized) {
    return 'неизвестен'
  }
  return WAZZUP_CHANNEL_STATUS_LABELS[normalized] || status.trim() || 'неизвестен'
}

/** Можно слать / выбирать канал: только active (и legacy connected). */
export const isWazzupChannelStatusUsable = (status: unknown): boolean => {
  const normalized = normalizeWazzupChannelStatus(status)
  return normalized === 'active' || normalized === 'connected'
}

export const isWazzupChannelSelectable = (
  channel: IntegrationChannelItem,
): boolean => {
  if (!isWazzupChannelStatusUsable(channel.status)) {
    return false
  }
  return String(channel.external_channel_id ?? '').trim().length > 0
}

/** Подпись под карточкой канала в picker (док / CRM). */
export const getWazzupChannelStatusHint = (
  channel: IntegrationChannelItem,
): string | null => {
  const normalized = normalizeWazzupChannelStatus(channel.status)
  if (!normalized || normalized === 'active' || normalized === 'connected') {
    return null
  }
  const label = getWazzupChannelStatusLabel(String(channel.status ?? ''))
  if (!label) {
    return null
  }
  return label.charAt(0).toUpperCase() + label.slice(1)
}

/** Семейство типов: telegram+tgapi, whatsapp+wapi и т.п. */
export const getWazzupChannelTypeFamily = (type: string): string => {
  const normalized = String(type ?? '')
    .trim()
    .toLowerCase()
  if (normalized === 'telegram' || normalized === 'tgapi') return 'telegram'
  if (normalized === 'whatsapp' || normalized === 'wapi') return 'whatsapp'
  if (
    normalized === 'max' ||
    normalized === 'maxbot' ||
    normalized === 'maxgroup'
  ) {
    return 'max'
  }
  if (normalized === 'vk' || normalized === 'vkontakte') return 'vk'
  return normalized || 'unknown'
}

const omitLeadFieldIdKey = (
  value: Record<string, unknown>,
): Record<string, unknown> => {
  const next = { ...value }
  delete next.lead_field_id
  return next
}

/**
 * Payload для update-channel: settings перезаписывается целиком.
 * `lead_field_id` не передаём — поле канала создаётся на бэке.
 */
export const buildWazzupChannelSettingsUpdatePayload = (
  channel: IntegrationChannelItem,
  options: {
    /** Если задан — подменить `crm` (или flat CRM-ключи). Иначе оставить как на канале. */
    crm?: IntegrationChannelCrmSettings | null
  },
): Record<string, unknown> => {
  const raw =
    channel.settings == null || channel.settings === ''
      ? null
      : tryParseJson<Record<string, unknown>>(channel.settings)

  const base: Record<string, unknown> =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? omitLeadFieldIdKey(raw)
      : {}

  if (options.crm === null) {
    delete base.crm
    return omitLeadFieldIdKey(base)
  }

  if (options.crm !== undefined) {
    if ('crm' in base || Object.keys(base).length === 0) {
      base.crm = options.crm
    } else {
      // Исторически settings могли приходить flat (без обёртки crm).
      Object.assign(base, options.crm)
    }
  }

  return omitLeadFieldIdKey(base)
}

export type { IntegrationStatus }
